import { armBatch, saveBatch, takeBatch } from 'lib/fast_pay_batch_snapshot';
import ApplicationController from './application_controller';
import { loadJQueryDataTables } from '../lib/datatables/load_jquery_datatables';

export default class extends ApplicationController {
  static targets = [
    'form',
    'invoiceTable',
    'amount',
    'paymentType',
    'hiddenPaymentFields',
    'accountNumber',
    'payableType',
    'reuseDetailsFromId',
    'selectedPatientInvoice',
    'summaryCount',
    'summaryTotal',
    'preview',
    'selectAll',
    'submit',
    'remainingBalance',
  ];

  static values = {
    options: Object, // optional JSON config from HTML
  };

  // Payment type used when a credit card PPM is selected
  static CARD_PAYMENT_TYPES = ['credit_card'];
  static FACILITY_PAYABLE_TYPE = 'FacilityInvoice';

  /**
   * Stimulus lifecycle hook. Runs before any target connected callbacks.
   * Sets up per-element bookkeeping for selectize change handlers and pending retries.
   */
  initialize() {
    // Per-element selectize change handlers so target disconnects can unbind
    // the exact handler that was bound. DataTables detaches/re-attaches the
    // same rows on pagination, re-firing targetConnected while the selectize
    // instance survives — without unbinding, one extra handler accumulates
    // per round-trip.
    this.selectizeChangeHandlers = new WeakMap();
    // Pending "wait for selectize to attach" retry timeout ids, keyed by element.
    this.selectizeRetryTimeouts = new Map();
  }

  /**
   * Stimulus lifecycle hook.
   * Initializes state, formats existing amount fields, and rebuilds selection state when navigating back via Turbo.
   */
  connect() {
    this.selectedPatientInvoices = new Map();
    window.isDirty = window.isDirty || false;
    // Auto-format all amounts on initial load
    this.amountTargets.forEach((input) => {
      if (input.value.trim() !== '') {
        input.value = this.formatAmount(input.value);
      }
    });
    // Rebuild selection state if coming back via browser history (give the checkboxes a bit to build)
    setTimeout(() => this.refreshSelectedPayments(), 150);

    // The Apply overpayment confirmation lives outside this form, so its confirm link is watched
    // at the document. Capture phase, so this runs before rails-ujs starts the request.
    this.armOnCreditApplyConfirm = this.armOnCreditApplyConfirm.bind(this);
    document.addEventListener('click', this.armOnCreditApplyConfirm, true);
    this.restoreBatchWhenReady();
  }

  /**
   * Stimulus lifecycle hook. Removes the document-level confirm watcher (the document outlives
   * this controller across Turbo visits) and cancels any pending selectize-attach retries so they
   * don't keep firing after the controller is gone.
   */
  disconnect() {
    document.removeEventListener('click', this.armOnCreditApplyConfirm, true);
    this.selectizeRetryTimeouts.forEach((timeoutId) => clearTimeout(timeoutId));
    this.selectizeRetryTimeouts.clear();
  }

  /**
   * Saves the batch being built when a row's Apply overpayment confirmation opens. Applying credit
   * posts and reloads the page; this is what lets the batch come back afterwards. Saving here does
   * not restore anything by itself: only confirming arms it (armOnCreditApplyConfirm).
   */
  saveBatchForCreditApply(event) {
    const rows = [...this.selectedPatientInvoices.values()].map((details) => ({
      ...details,
      remaining_balance: this.currentRemainingBalance(details.payable_id),
    }));
    saveBatch({ url: this.pageKey, applyingInvoiceId: event.params.invoiceId, rows });
  }

  armOnCreditApplyConfirm(event) {
    if (!(event.target instanceof Element) || !event.target.closest('[data-fast-pay-credit-apply]')) return;

    armBatch();
    // The batch is kept, so leaving is safe: no unsaved changes warning for this navigation.
    window.isDirty = false;
    this.formTarget.dispatchEvent(new CustomEvent('mark-clean'));
  }

  get pageKey() {
    return `${window.location.pathname}${window.location.search}`;
  }

  // DataTables takes rows off the current page out of the document, so rows are found through its
  // row nodes rather than Stimulus targets, which only see rows in the document.
  rowNode(invoiceId) {
    const nodes = this.dt ? this.dt.rows().nodes().toArray() : Array.from(this.element.querySelectorAll('tr'));
    return nodes.find((tr) =>
      tr.querySelector(`[data-fast-pay-target="selectedPatientInvoice"][data-invoice-id="${invoiceId}"]`)
    );
  }

  currentRemainingBalance(invoiceId) {
    return this.rowNode(invoiceId)?.querySelector('[data-fast-pay-target="remainingBalance"]')?.value;
  }

  /**
   * Waits for DataTables and the visible Selectize dropdowns, then restores the batch saved before
   * Apply overpayment, if there is one for this page.
   */
  restoreBatchWhenReady(attempt = 0) {
    const selectizeReady = this.paymentTypeTargets.every((select) => select.selectize);
    if ((!this.dt || !selectizeReady) && attempt < 30) {
      setTimeout(() => this.restoreBatchWhenReady(attempt + 1), 100);
      return;
    }

    const snapshot = takeBatch({ url: this.pageKey });
    if (snapshot) this.restoreBatch(snapshot);
  }

  /**
   * Puts each saved row back when it can be trusted: the row is still listed and selectable, its
   * chosen payment method is still offered, and its balance is unchanged. A changed balance means
   * something happened to that invoice, most often the credit just applied, so the biller decides
   * afresh instead of collecting an amount chosen against the old balance.
   */
  restoreBatch({ rows }) {
    let skipped = 0;

    rows.forEach(({ remaining_balance: savedBalance, ...details }) => {
      const invoiceId = details.payable_id;
      const node = this.rowNode(invoiceId);
      const checkbox = node?.querySelector('[data-fast-pay-target="selectedPatientInvoice"]');
      const balance = node?.querySelector('[data-fast-pay-target="remainingBalance"]')?.value;
      const radios = node ? Array.from(node.querySelectorAll('[data-fast-pay-target="reuseDetailsFromId"]')) : [];
      const method = radios.find((radio) => radio.value === details.reuse_details_from_id);

      if (!node || !checkbox || checkbox.disabled || balance !== savedBalance || (radios.length && !method)) {
        skipped += 1;
        return;
      }

      checkbox.checked = true;
      const amount = node.querySelector('[data-fast-pay-target="amount"]');
      if (amount && details.amount !== undefined) amount.value = details.amount;
      if (method) {
        radios.forEach((radio) => {
          radio.checked = radio === method;
        });
        if (method.dataset.paymentMethodType && this.find(invoiceId, 'paymentType')) {
          this.filterPaymentTypes(invoiceId, method.dataset.paymentMethodType);
        }
      }
      this.restoreSelectValue(node.querySelector('[data-fast-pay-target="paymentType"]'), details.payment_type);
      this.restoreSelectValue(node.querySelector('[data-fast-pay-target="accountNumber"]'), details.account_number);

      this.selectedPatientInvoices.set(invoiceId, details);
    });

    const restored = this.selectedPatientInvoices.size;
    this.updateHiddenPaymentFields();
    this.validateSubmitState();
    window.isDirty = restored > 0;
    if (restored > 0) {
      this.formTarget.dispatchEvent(new CustomEvent('mark-dirty'));
      this.showRestoredNotice(skipped);
    }
  }

  // Selectize keeps its own value; a row not yet shown has no Selectize, so its <select> is set and
  // Selectize picks the value up when the row is drawn.
  restoreSelectValue(select, value) {
    if (!select || value === undefined || value === '') return;

    if (select.selectize) {
      select.selectize.addOption({ value, text: value });
      select.selectize.setValue(value, true);
    } else {
      if (![...select.options].some((option) => option.value === value)) select.add(new Option(value, value));
      select.value = value;
    }
  }

  showRestoredNotice(skipped) {
    const notice = document.createElement('div');
    notice.className = 'alert alert-info';
    notice.setAttribute('role', 'alert');
    notice.dataset.fastPayRestoredNotice = '';
    const icon = document.createElement('i');
    icon.className = 'bi bi-info-circle mr-2';
    notice.appendChild(icon);
    let text = 'Your selected rows were kept after applying credit.';
    if (skipped > 0) {
      text += ` ${skipped} ${skipped === 1 ? 'row was' : 'rows were'} not reselected because ${skipped === 1 ? 'its balance' : 'their balances'} changed.`;
    }
    notice.appendChild(document.createTextNode(text));
    this.formTarget.prepend(notice);
  }

  amountChanged(event) {
    const amount = event.target;
    amount.value = this.formatAmount(amount.value);
    this.refreshSelectedPayments();
  }

  /**
   * Handles radio button (payment method) changes.
   * Filters the payment type dropdown to show only relevant options for the selected PPM.
   */
  change(event) {
    const radio = event.target;
    const { invoiceId, paymentMethodType } = radio.dataset;
    if (paymentMethodType) {
      this.filterPaymentTypes(invoiceId, paymentMethodType);
    }
    this.refreshSelectedPayments();
  }

  /**
   * Finds a single target for a given invoice ID.
   */
  find(invoiceId, name) {
    return this[`${name}Targets`].find((x) => x.dataset.invoiceId === invoiceId);
  }

  /**
   * Finds all targets for a given invoice ID.
   */
  findAll(invoiceId, name) {
    return this[`${name}Targets`].filter((x) => x.dataset.invoiceId === invoiceId);
  }

  /**
   * Initializes DataTables when the invoice table appears.
   * Supports both "new" and "preview" layouts by inspecting column count.
   */
  async invoiceTableTargetConnected(table) {
    await loadJQueryDataTables();
    if (!table.isConnected) return;

    const $ = window.jQuery || window.$;
    if (this.dt) this.dt.destroy();
    // Used by two views; New and Preview.
    const columnCount = table.querySelectorAll('thead th').length;
    let columnDefs;
    if (columnCount === 4) {
      columnDefs = [
        { targets: 0, orderable: false },
        { targets: 1, className: 'fast-pay-invoice-column' },
        { targets: 2, className: 'fast-pay-payment-type-column', orderable: false },
        { targets: 3, className: 'fast-pay-amount-column' },
      ];
    } else {
      columnDefs = [
        { targets: 0, className: 'fast-pay-invoice-column-preview' },
        { targets: 1, className: 'fast-pay-payment-type-column' },
        { targets: 2, className: 'fast-pay-amount-column' },
      ];
    }

    const defaults = {
      responsive: true,
      order: [[columnCount === 4 ? 1 : 0, 'asc']],
      pageLength: 25,
      columnDefs,
      autoWidth: false,
      drawCallback: () => {
        // this.$ is the jQuery instance that has selectize attached — see paymentTypeTargetConnected.
        // window.$ and window.jQuery are different instances that do NOT have selectize.
        // On the initial draw (page 1) this.$ isn't set yet; turbo:load handles page 1.
        // On subsequent draws (pagination), this.$ is set and we initialize any bare selects.
        if (!this.$) return;
        this.$(table).find('.select.filterable-text-with-create:not(.selectized)').selectize({
          create: true,
          sortField: 'text',
        });
      },
    };

    const config = { ...defaults, ...(this.optionsValue || {}) };
    this.dt = $(table).DataTable(config);
  }

  /**
   * Cleans up DataTable instance when controller disconnects.
   */
  invoiceTableTargetDisconnected(table) {
    if (this.dt) {
      const $ = window.jQuery || window.$;
      if ($.fn.dataTable.isDataTable(table)) this.dt.destroy();
      this.dt = null;
    }
  }

  /**
   * Builds a normalized payment payload for a given invoice. Used when constructing hidden inputs for form submission.
   */
  paymentDetails(invoiceId) {
    const amount = this.find(invoiceId, 'amount')?.value;
    const paymentType = this.find(invoiceId, 'paymentType')?.selectize?.getValue();
    const payableType = this.find(invoiceId, 'payableType')?.value;
    const reuseDetailsFromType = payableType === this.constructor.FACILITY_PAYABLE_TYPE ? 'FacilityInvoicePayment' : 'PatientPaymentMethod';
    const reuseDetailsFromId = this.findAll(invoiceId, 'reuseDetailsFromId').find(
      (reuseDetailsFrom) => reuseDetailsFrom.checked
    )?.value;
    const accountNumber = this.find(invoiceId, 'accountNumber').value;

    const details = {
      payable_id: invoiceId,
      payable_type: payableType,
      amount,
      reuse_details_from_id: reuseDetailsFromId,
      reuse_details_from_type: reuseDetailsFromType,
      payment_type: paymentType,
      account_number: accountNumber,
    };

    for (const key in details) {
      if (details[key] === undefined) {
        delete details[key];
      }
    }

    return details;
  }

  /**
   * Hook for Selectize-backed payment type dropdowns. Ensures change events propagate into Stimulus.
   */
  paymentTypeTargetConnected(paymentType) {
    if (!paymentType.selectize) {
      // Selectize hasn't initialized yet. Retry shortly.
      this.scheduleSelectizeRetry(paymentType, () => this.paymentTypeTargetConnected(paymentType));
      return;
    }
    this.cancelSelectizeRetry(paymentType);

    // Acquire the jQuery constructor from the first Selectize instance we find.
    if (!this.$) {
      this.$ = paymentType.selectize.$input.constructor;
    }

    // Bind to the Selectize change event so updates propagate into Stimulus.
    this.bindSelectizeChange(paymentType, this.typeChanged.bind(this, paymentType));

    // Apply initial filtering based on any pre-selected radio button for this invoice
    const { invoiceId } = paymentType.dataset;
    if (invoiceId) {
      const selectedRadio = this.findAll(invoiceId, 'reuseDetailsFromId').find((r) => r.checked);
      if (selectedRadio?.dataset.paymentMethodType) {
        this.filterPaymentTypes(invoiceId, selectedRadio.dataset.paymentMethodType);
      }
    }
  }

  /**
   * Unbinds the Selectize change handler when DataTables detaches the row
   * (pagination) or the controller disconnects, so re-connects don't stack handlers.
   */
  paymentTypeTargetDisconnected(paymentType) {
    this.cancelSelectizeRetry(paymentType);
    this.unbindSelectizeChange(paymentType);
  }

  /**
   * Hook for Selectize-backed account number dropdowns. Ensures change events propagate into Stimulus.
   */
  accountNumberTargetConnected(accountNumber) {
    if (!accountNumber.selectize) {
      this.scheduleSelectizeRetry(accountNumber, () => this.accountNumberTargetConnected(accountNumber));
      return;
    }
    this.cancelSelectizeRetry(accountNumber);

    this.bindSelectizeChange(accountNumber, () => this.refreshSelectedPayments());
  }

  /**
   * Unbinds the Selectize change handler when DataTables detaches the row
   * (pagination) or the controller disconnects, so re-connects don't stack handlers.
   */
  accountNumberTargetDisconnected(accountNumber) {
    this.cancelSelectizeRetry(accountNumber);
    this.unbindSelectizeChange(accountNumber);
  }

  /**
   * Binds a change handler to an element's Selectize instance, tracking the
   * handler per element so it can be unbound later. No-ops if the element
   * already has a bound handler (guards against double-binding).
   */
  bindSelectizeChange(element, handler) {
    if (this.selectizeChangeHandlers.has(element)) return;

    this.selectizeChangeHandlers.set(element, handler);
    element.selectize.on('change', handler);
  }

  /**
   * Removes the tracked Selectize change handler for an element, if any.
   * Selectize's MicroEvent mixin provides `off(event, handler)`.
   */
  unbindSelectizeChange(element) {
    const handler = this.selectizeChangeHandlers.get(element);
    if (!handler) return;

    this.selectizeChangeHandlers.delete(element);
    if (element.selectize) {
      element.selectize.off('change', handler);
    }
  }

  /**
   * Schedules a one-shot retry for elements whose Selectize instance hasn't
   * attached yet. Skips scheduling when the element has left the DOM
   * (DataTables detach, Turbo navigation) so retries can't chain forever.
   */
  scheduleSelectizeRetry(element, retry) {
    if (!element.isConnected) return;

    this.cancelSelectizeRetry(element);
    const timeoutId = setTimeout(() => {
      this.selectizeRetryTimeouts.delete(element);
      retry();
    }, 100);
    this.selectizeRetryTimeouts.set(element, timeoutId);
  }

  /**
   * Cancels a pending Selectize-attach retry for an element, if any.
   */
  cancelSelectizeRetry(element) {
    const timeoutId = this.selectizeRetryTimeouts.get(element);
    if (timeoutId === undefined) return;

    clearTimeout(timeoutId);
    this.selectizeRetryTimeouts.delete(element);
  }

  /**
   * Selects or deselects all invoices currently visible in the table.
   */
  selectAll(event) {
    const offPageElements = [];
    const onScreenIds = new Set(this.selectedPatientInvoiceTargets.map((x) => x.dataset.invoiceId));
    const { checked } = event.target;

    this.dt.rows({ search: 'applied' }).every(function () {
      const row = $(this.node());
      row.find('[data-fast-pay-target]').each((index, element) => {
        // Skip disabled checkboxes (invoices without an available payment method)
        if (element.dataset.fastPayTarget === 'selectedPatientInvoice' && !element.disabled) {
          element.checked = checked;
        }
        // Grab the off screen ones
        if (!onScreenIds.has(element.dataset.invoiceId)) offPageElements.push(element.cloneNode());
      });
    });

    // Append the off page elements so we can use the same code flow
    offPageElements.forEach((element) => this.formTarget.appendChild(element));
    // Trigger the toggle event to prep them for submission
    this.selectedPatientInvoiceTargets.forEach((x) => this.toggleSelectPatientInvoice(x, false));
    // Update all of the data at one time instead of each invoice individually
    this.updateHiddenPaymentFields();
    this.validateSubmitState();
    // Off page elements are no longer needed
    offPageElements.forEach((x) => x.remove());
  }

  /**
   * Wrapper for checkbox change events.
   */
  toggle(event) {
    this.refreshSelectedPayments();
  }

  /**
   * Syncs Selectize changes into the internal model.
   */
  typeChanged(originalElement) {
    originalElement.value = originalElement.selectize?.getValue();
    this.refreshSelectedPayments();
  }

  /**
   * Adds or removes an invoice from the selected set.
   */
  toggleSelectPatientInvoice(selectPatientInvoice, updateHidden = true) {
    const { invoiceId } = selectPatientInvoice.dataset;
    if (invoiceId) {
      if (selectPatientInvoice.checked) {
        this.selectedPatientInvoices.set(invoiceId, this.paymentDetails(invoiceId));
      } else {
        this.selectedPatientInvoices.delete(invoiceId);
      }
      // Update form fields used to submit the payment information
      if (updateHidden) {
        this.updateHiddenPaymentFields();
      }
      // if something is checked
      window.isDirty = true;
    }
  }

  /**
   * Rebuilds hidden inputs used for form submission. Also recalculates totals and selected count.
   */
  updateHiddenPaymentFields() {
    if (this.hasHiddenPaymentFieldsTarget) {
      this.hiddenPaymentFieldsTarget.innerHTML = '';

      [...this.selectedPatientInvoices.values()].forEach((details, index) => {
        let input;
        for (const [field, value] of Object.entries(details)) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = `fast_pay[payments_attributes][${index}][${field}]`;
          input.value = value;
          this.hiddenPaymentFieldsTarget.appendChild(input);
        }
      });
    }

    const total = Array.from(this.selectedPatientInvoices.values()).reduce(
      (sum, payment) => sum + Number(payment.amount.replace(/,/g, '')),
      0
    );

    this.summaryTotalTarget.innerHTML = `$${total.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

    this.summaryCountTarget.innerHTML = this.selectedPatientInvoices.size;
  }

  /**
   * Formats raw numeric input into a currency string.
   */
  formatAmount(raw) {
    return [raw.replace(/\D/g, '').padStart(3, '0')]
      .map((s) => [Number(s.slice(0, -2)), s.slice(-2)])[0]
      .join('.');
  }

  /**
   * Filters the payment type Selectize dropdown based on the selected PPM type.
   * Credit card PPMs show only credit_card/debit_card options; non-card PPMs show all options.
   */
  filterPaymentTypes(invoiceId, paymentMethodType) {
    const paymentTypeEl = this.find(invoiceId, 'paymentType');
    if (!paymentTypeEl?.selectize) return;

    const selectize = paymentTypeEl.selectize;

    // Store original options on first call so we can restore them later.
    // Only keep value/text to avoid Selectize internal state ($order, etc.) from leaking across rebuilds.
    if (!this._originalPaymentTypeOptions) this._originalPaymentTypeOptions = {};
    if (!this._originalPaymentTypeOptions[invoiceId]) {
      this._originalPaymentTypeOptions[invoiceId] = Object.values(selectize.options).map((opt) => ({
        value: opt.value,
        text: opt.text,
      }));
    }

    const allOptions = this._originalPaymentTypeOptions[invoiceId];
    const isCard = paymentMethodType === 'credit_card';
    const cardTypes = this.constructor.CARD_PAYMENT_TYPES;

    // Card PPM → only card types; non-card PPM → exclude card types
    const validOptions = isCard
      ? allOptions.filter((opt) => cardTypes.includes(opt.value))
      : allOptions.filter((opt) => !cardTypes.includes(opt.value));

    // Rebuild the Selectize options.
    // clear() deselects the current item first — clearOptions() skips selected items.
    const currentValue = selectize.getValue();
    selectize.clear(true);
    selectize.clearOptions();
    validOptions.forEach((opt) => selectize.addOption(opt));
    selectize.refreshOptions(false);

    // Restore the previous value if it still makes sense for the selected PPM,
    // otherwise default to the first valid option.
    const currentStillValid = validOptions.find((opt) => opt.value === currentValue);
    const currentIsCard = cardTypes.includes(currentValue);
    if (currentStillValid && (isCard || !currentIsCard)) {
      selectize.setValue(currentValue, true);
    } else {
      selectize.setValue(validOptions[0]?.value, true);
    }
  }

  /**
   * Rebuilds the selected payment state based on currently checked invoices.
   * This method is used when amounts or related fields change without toggling
   * the checkbox itself, ensuring totals and hidden fields stay in sync.
   */
  refreshSelectedPayments() {
    // Only update entries for checkboxes visible in the DOM.
    // Off-page DataTable rows are removed from the DOM, so clearing the entire
    // Map would lose selections made on other pages.
    this.selectedPatientInvoiceTargets.forEach((checkbox) => {
      const { invoiceId } = checkbox.dataset;
      if (checkbox.checked) {
        this.selectedPatientInvoices.set(invoiceId, this.paymentDetails(invoiceId));
      } else {
        this.selectedPatientInvoices.delete(invoiceId);
      }
    });
    window.isDirty = this.selectedPatientInvoices.size > 0;
    this.updateHiddenPaymentFields();
    this.validateSubmitState();
  }

  /**
   * Enables Submit only when every selected invoice has a chosen payment method.
   * Sole-method invoices auto-supply a hidden radio so they always pass.
   * Multi-method invoices require an explicit radio selection.
   */
  validateSubmitState() {
    if (!this.hasSubmitTarget) return;
    const hasSelections = this.selectedPatientInvoices.size > 0;
    // Facility invoices don't use patient payment methods, so they don't need a radio selection.
    const allHaveMethod = Array.from(this.selectedPatientInvoices.values()).every(
      (details) => details.payable_type === this.constructor.FACILITY_PAYABLE_TYPE || details.reuse_details_from_id
    );
    this.submitTarget.disabled = !hasSelections || !allHaveMethod;
    this.submitTarget.title =
      hasSelections && !allHaveMethod ? 'Select a payment method for each invoice.' : '';
  }

  /**
   * Intercepts filter/reset actions if the form has unsaved selections.
   */
  confirmIfDirty(event) {
    if (!window.isDirty) return;

    const message =
      event.currentTarget.dataset.confirmMessage ||
      'Applying filters will reload the page and you will lose your current selections and any entered payment data.\n\nDo you want to continue?';

    if (!window.confirm(message)) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }
}
